import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { FomanticShim, PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { DateHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/DateHelper";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { IJNativeEvent, JNativeStatic } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { Property } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ApplicationInformationService } from "@seriously-rad/seriouslyrad-framework-core/RadWindow";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { IPanel, Panel, PanelPropertyValues } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Panel";
import { PageManager } from "@seriously-rad/seriouslyrad-framework-core/Ui/PageManager";
import { INotification } from "../../UserNotificationManager";
import { UserNotification } from "./UserNotification";
import { NAMESPACE } from "./_Namespace";
import { UserNotificationManagerService } from "../../UserNotificationManagerService";

import "./UserNotificationPanel.scss";

export type UserNotificationPanelPropertyValues =
    PanelPropertyValues
    & {
    };

export type UserNotificationPanelProperties = {
    [name in keyof UserNotificationPanelPropertyValues]: Property<UserNotificationPanelPropertyValues[name]>
};

export interface IUserNotificationPanel extends IPanel, UserNotificationPanelProperties
{
}

export class UserNotificationPanel extends Panel implements IUserNotificationPanel
{
    public static override classNamespace = NAMESPACE + "UserNotificationPanel";
    public static override className = "UserNotificationPanel";

    // #region Global Variables

    protected closeTimerTimeoutId?: number

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<UserNotificationPanelPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public addNotification(notification: INotification)
    {
        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        const
            createdDate = notification.createdDate ? DateHelper.fromString(notification.createdDate, "yyyy-MM-ddTHH:mm:ss.fffffffZ") : null;

        new UserNotification({
            tag: notification,
            targetUrl: notification.targetUrl,
            text: notification.message,
            title: notification.title,
            subTitle: createdDate ? DateHelper.toString(DateHelper.convertToUtc(createdDate), "dd/MM/yyyy HH:mm:ss") : null
        }).appendTo(this);

        $$element[0].scrollTop = $$element[0].scrollHeight;

        if (!this.visible.get())
        {
            FomanticShim($$element)
                .transition("fly left in");

            this.visible.set(true);
        }

        this._setCloseTimer();
    }

    // #endregion

    // #region Protected Methods

    protected _setCloseTimer()
    {
        if (this.closeTimerTimeoutId)
        {
            clearTimeout(this.closeTimerTimeoutId);
            this.closeTimerTimeoutId = 0;
        }

        if (!this.closeTimerTimeoutId)
        {
            this.closeTimerTimeoutId = window.setTimeout(() =>
            {
                this.closeTimerTimeoutId = 0;
                FomanticShim(this.$$element.get()).transition({
                    animation: "fly left out",
                    onComplete: () => this.controlsClear(),
                });
                this.visible.set(false)
            }, 5000);
        }
    }

    // #endregion

    // #region Overrides

    protected override _bindEvents()
    {
        super._bindEvents();

        this.on("userNotificationButtonClicked", { self: this }, this.onUserNotificationButtonClicked);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement(),
            $$containerElement =
                JNativeStatic.create$$({ nodeName: "div", attributes: { "class": "ui cards" } })
                    .appendTo($$element);

        this._containerElementRef.current = $$containerElement;

        return $$element;
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();

        this.addCssClass("rad-control-user-notification-panel");
        this.$$element.get()?.appendTo("body");
        this.visible.set(false);
    }

    // #endregion

    // #region Event Handlers

    protected async onUserNotificationButtonClicked(evt: IJNativeEvent, eventData: any)
    {
        evt.stopPropagation();

        const
            self = evt.data.self as this,
            buttonTag = eventData.button ? eventData.button.tag.get() : null,
            userNotificationTag = eventData.notification ? eventData.notification.tag.get() : null;

        self._setCloseTimer();

        if (!buttonTag || !userNotificationTag)
            return;

        await ServiceManager.getOptional(UserNotificationManagerService)?.dismissNotificationAsync(userNotificationTag.id);
        await eventData.notification.removeAsync();

        if (buttonTag !== "open")
            return;

        const
            targetUrl = eventData.notification.targetUrl.get();

        if (!targetUrl)
            return;

        await PageManager.instance?.navigateAsync(StringHelper.format("{0}{1}", ServiceManager.getRequired(ApplicationInformationService).virtualDirectory, targetUrl));
    }

    // #endregion
}

